import { useState, memo } from 'react';
import { ScrollView } from 'react-native';
import { YStack } from 'tamagui';
import { H4 } from 'tamagui';
import { Card } from 'tamagui';
import { H6 } from 'tamagui';
import { Pressable } from 'react-native';
import { View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { SizableText } from 'tamagui';

interface SettingsTabProps {
  theme: boolean;
}

const SettingsTab = memo(({ theme }: SettingsTabProps) => {
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [autoPublish, setAutoPublish] = useState(false);
  const [defaultPlatform, setDefaultPlatform] = useState('all');

  const settingsSections = [
    {
      title: 'Publishing',
      items: [
        {
          label: 'Auto-publish scheduled posts',
          value: autoPublish,
          onToggle: setAutoPublish,
          icon: 'publish',
          type: 'toggle',
          description: 'Automatically publish posts at scheduled times'
        },
        {
          label: 'Default platform',
          value: defaultPlatform,
          onValueChange: setDefaultPlatform,
          icon: 'public',
          type: 'select',
          options: ['all', 'facebook', 'instagram', 'twitter', 'linkedin']
        }
      ]
    },
    {
      title: 'Notifications',
      items: [
        {
          label: 'Enable notifications',
          value: notificationsEnabled,
          onToggle: setNotificationsEnabled,
          icon: 'notifications',
          type: 'toggle',
          description: 'Receive notifications for post engagement'
        }
      ]
    },
    {
      title: 'Content',
      items: [
        {
          label: 'Default hashtags',
          icon: 'tag',
          type: 'action',
          onPress: () => console.log('Edit default hashtags')
        },
        {
          label: 'Content templates',
          icon: 'description',
          type: 'action',
          onPress: () => console.log('Manage templates')
        }
      ]
    }
  ];

  return (
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={{ padding: 20, gap: 24 }}
      showsVerticalScrollIndicator={false}
    >
      <YStack gap={24}>
        <H4 style={{ color: theme ? "#1e293b" : "#f1f5f9", fontWeight: '700' }}>
          Settings
        </H4>

        {settingsSections.map((section, sectionIndex) => (
          <Card
            key={sectionIndex}
            style={{
              backgroundColor: theme ? "#ffffff" : "#1e293b",
              borderRadius: 16,
              padding: 20,
              borderWidth: 1,
              borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
            }}
          >
            <H6 style={{
              color: theme ? "#1e293b" : "#f1f5f9",
              marginBottom: 16,
              fontWeight: '700'
            }}>
              {section.title}
            </H6>
            <YStack gap={12}>
              {section.items.map((item, itemIndex) => (
                <Pressable
                  key={itemIndex}
                  onPress={item.type === 'action' ? item.onPress : undefined}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    paddingVertical: 12,
                    borderBottomWidth: itemIndex < section.items.length - 1 ? 1 : 0,
                    borderBottomColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
                  }}
                >
                  <View style={{
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                    backgroundColor: theme ? "#f0f0f0" : "#2d3748",
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: 12
                  }}>
                    <MaterialIcons
                      name={item.icon as any}
                      size={20}
                      color={theme ? "#64748b" : "#94a3b8"}
                    />
                  </View>
                  <YStack flex={1}>
                    <SizableText style={{
                      color: theme ? "#1e293b" : "#f1f5f9",
                      fontWeight: '600',
                      fontSize: 14
                    }}>
                      {item.label}
                    </SizableText>
                    {item.description && (
                      <SizableText style={{
                        color: theme ? "#64748b" : "#94a3b8",
                        fontSize: 12,
                        marginTop: 2
                      }}>
                        {item.description}
                      </SizableText>
                    )}
                  </YStack>
                  {item.type === 'toggle' && (
                    <Pressable
                      onPress={() => item.onToggle?.(!item.value)}
                      style={{
                        width: 50,
                        height: 30,
                        borderRadius: 15,
                        backgroundColor: item.value
                          ? (theme ? "#4caf50" : "#81c784")
                          : (theme ? "#e0e0e0" : "#374151"),
                        justifyContent: 'center',
                        paddingHorizontal: 2
                      }}
                    >
                      <View style={{
                        width: 26,
                        height: 26,
                        borderRadius: 13,
                        backgroundColor: 'white',
                        transform: [{ translateX: item.value ? 20 : 0 }]
                      }} />
                    </Pressable>
                  )}
                  {item.type === 'action' && (
                    <MaterialIcons
                      name="chevron-right"
                      size={24}
                      color={theme ? "#64748b" : "#94a3b8"}
                    />
                  )}
                </Pressable>
              ))}
            </YStack>
          </Card>
        ))}

        {/* Additional Actions */}
        <Card style={{
          backgroundColor: theme ? "#ffffff" : "#1e293b",
          borderRadius: 16,
          padding: 20,
          borderWidth: 1,
          borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
        }}>
          <YStack gap={12}>
            <Pressable
              onPress={() => console.log('Export data')}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                paddingVertical: 12
              }}
            >
              <MaterialIcons
                name="download"
                size={24}
                color={theme ? "#64748b" : "#94a3b8"}
                style={{ marginRight: 12 }}
              />
              <SizableText style={{
                color: theme ? "#1e293b" : "#f1f5f9",
                fontWeight: '600',
                fontSize: 14
              }}>
                Export Data
              </SizableText>
            </Pressable>
            <View style={{
              height: 1,
              backgroundColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
            }} />
            <Pressable
              onPress={() => console.log('Clear cache')}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                paddingVertical: 12
              }}
            >
              <MaterialIcons
                name="delete-outline"
                size={24}
                color={theme ? "#ef5350" : "#e57373"}
                style={{ marginRight: 12 }}
              />
              <SizableText style={{
                color: theme ? "#ef5350" : "#e57373",
                fontWeight: '600',
                fontSize: 14
              }}>
                Clear Cache
              </SizableText>
            </Pressable>
          </YStack>
        </Card>
      </YStack>
    </ScrollView>
  );
});

export default SettingsTab;
